import { useCallback, useMemo } from "react";

import { SELF_SERVICE_PLANS } from "@/constants";
import { useOrganization } from "@/hooks/useOrganization";

import useSearchParams from "./useSearchParams";

/**
 * Hook intended to wrap onClicks which require some condition to be true.
 *
 * Caller is responsible for handling undefined responses; may lead to perceived bugs if left unhandled.
 */
export const useConditionFlaggedCallback = (
  allow: boolean,
  callback?: () => void,
  attemptUpsell = false
) => {
  const { data: organization } = useOrganization();
  const plan = organization?.ok ? organization.data.plan : null;
  const isSelfService = useMemo(
    () => plan && SELF_SERVICE_PLANS.includes(plan.type),
    [plan]
  );
  const [_, setSearchParams] = useSearchParams<{ upsale?: boolean }>();

  const upsellCallback = useCallback(
    () => setSearchParams({ upsale: true }),
    [setSearchParams]
  );

  const conditionalCallback = useMemo(() => {
    if (allow) return callback ?? (() => {});
    if (attemptUpsell && isSelfService) return upsellCallback;
    return undefined;
  }, [allow, callback, attemptUpsell, isSelfService, upsellCallback]);

  return conditionalCallback;
};
